import { useCallback } from 'react'
import { Box } from '@mui/material'
// @ts-ignore
import hostConf from '$config/frontend'
// Components
import { useHall } from '$logic/hall/react-hook'
import {
  useClientUserPermissions,
  useSubscription,
} from '$logic/subscription/react-hook'
import { useRoom } from '$logic/room/react-hook'
import {
  closeHallProfile,
  openHallSettings,
  switchToGeneralIssue,
} from '$logic/router'
import { joinHall, setSubscriptionMute } from '$logic/subscription'
import { openRoom } from '$logic/opened-room'
// Common
import { HallProfileInfo } from '$common/hall-profile-info'
import { RoomProfileHeader } from '$common/room-profile-header'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { SettingsSection } from '$common/settings-section'
import { NotificationButton } from '$common/notification-button'
import { ListItemTextButton } from '$common/buttons/list-item-text-button'
import { ArrowIOSIcon, VitrineIcon } from '$common/icons'
// Helpers
import { toLocale } from '$func/text'
// Localization
import { stringToColor } from '$func/string-to-color'
import { t } from '$i18n'
import { ActionButton } from '$comp/_app/components/chat/components/footer/components/action-button'
import { HallAvatar } from '$common/avatar'
import { OpenStorySource } from '$logic/story/analytics'
import { HallProfileActions } from './hall-profile-actions'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { IS_STORY_ENABLED } from '$func/environmentInfo'
import { useWorkspaceColorByRoomId } from '$logic/workspace'

const hidePrivateHallMemberCount = hostConf.VITE_HIDE_PRIVATE_HALL_MEMBER_COUNT

interface Props {
  roomId: string
}

export function HallProfile({ roomId }: Props) {
  const { entity: subscription } = useSubscription(roomId)
  const { entity: hall } = useHall(roomId)

  const { muted } = subscription || {}
  const room = useRoom(roomId)

  const toggleNotificationStatus = useCallback(async () => {
    // TODO: Show loading modal
    await setSubscriptionMute(roomId, !muted)
  }, [muted, roomId])

  const onJoinClick = useCallback(async () => {
    analite('CHAT_JOIN', { source: 'HALL_PROFILE', name: hall?.identifier })
    // TODO: Show loading modal
    await joinHall(roomId)
    await openRoom(roomId)
  }, [roomId, hall?.identifier])

  const clientUserPermissions = useClientUserPermissions(roomId)
  const showMenu = !(clientUserPermissions?.isOwner && !hall?.public)

  const avatarBackgroundColor = stringToColor(hall?.name, roomId)
  const backgroundColor = useWorkspaceColorByRoomId(roomId)

  return (
    // TODO: CHECK HALL AVATAR COMPONENT
    <PageWithAppBar
      appBar={
        <RoomProfileHeader
          avatarElement={
            <HallAvatar
              roomId={roomId}
              canClientUserCreateStory={
                clientUserPermissions?.isAdmin && room?.type === 'CHANNEL'
              }
              withStory={IS_STORY_ENABLED}
              size={AvatarSize.XL}
              source={OpenStorySource.ROOM_PROFILE}
            />
          }
          primaryText={hall?.name || ''}
          avatarBackground={avatarBackgroundColor}
          hasDeleted={!!hall?.deletedAt}
          onBack={closeHallProfile}
          secondaryText={
            hall?.membersCount && (!hidePrivateHallMemberCount || hall?.public)
              ? `${toLocale(hall.membersCount)} ${t(
                  `profile.hall.${
                    room?.type === 'CHANNEL' ? 'subscriber' : 'member'
                  }`,
                )}`
              : ' '
          }
          roomType={room?.type}
          showMenuIcon={showMenu}
          hallId={roomId}
          onSettings={
            clientUserPermissions?.isAdmin
              ? () => openHallSettings(roomId)
              : undefined
          }
          backgroundColor={backgroundColor}
        />
      }
    >
      <Box sx={{ flexGrow: 1, overflow: 'hidden auto' }}>
        <SettingsSection>
          <>
            {hall && <HallProfileInfo hallId={roomId} />}
            {subscription && (
              <NotificationButton
                muted={muted}
                onClick={toggleNotificationStatus}
              />
            )}
          </>
        </SettingsSection>

        {!subscription && hall?.public && (
          <SettingsSection>
            <ListItemTextButton
              onClick={onJoinClick}
              value={
                room?.type === 'CHANNEL'
                  ? t('profile.hall.actions.joinChannel')
                  : t('profile.hall.actions.joinGroup')
              }
            />
          </SettingsSection>
        )}

        {/* TODO : Check upper line condition if is satisfied in hall-profile-actions */}
        <SettingsSection>
          <HallProfileActions
            hallId={roomId}
            isGroup={room?.type === 'GROUP'}
            permissions={clientUserPermissions}
          />
        </SettingsSection>
      </Box>
      {!!hall?.public && subscription
        ? null
        : // <ActionButton // NOTE: this should be when !!hall.public && subscription instead of null
          //   startIcon={<VitrineIcon sx={{ color: 'neutral900' }} />}
          //   endIcon={<ArrowIOSIcon sx={{ color: 'neutral900' }} />}
          //   text={t('chat.generalIssue')}
          //   onClick={onGeneralIssueButtonClick}
          //   backgroundColor="yellow500"
          //   color="neutral900"
          // />
          null}
      {!subscription && hall?.public && (
        <Box
          sx={{
            bgcolor: 'white',
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'neutral300',
          }}
        >
          <ActionButton
            onClick={onJoinClick}
            text={
              room?.type === 'CHANNEL'
                ? t('profile.hall.actions.joinChannel')
                : t('profile.hall.actions.joinGroup')
            }
          />
        </Box>
      )}
    </PageWithAppBar>
  )
}
